import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { queryClient } from '@/queries';
import {
  caseQueries,
  getCsStatusMutation,
  getDeviceStatusMutation,
  getNextCaseStatusMutation,
  getNextRouteStatusMutation,
  getSurgeonStatusMutation,
  reconcileCaseAcrossListCaches,
  refetchDuplicatesIfCaseClosed,
  removeCaseFromAllListCaches,
  retryDelay,
  shouldRetryOnError,
  toClinicalRecord,
  updateCaseCache,
  updateCaseDuplicatesCache,
  updateCaseField,
  updateClinicalInCaseCache,
} from '@/queries/case';
import {
  CaseStatus,
  CreateDraftCaseType,
  RoutePlanStatus,
  CreateTxCenterDraftCaseType,
  NewCase,
  DeclineCaseParams,
  LinkBody,
  ResourceLogisticsStatus,
  WOLSType,
  NRPType,
  OpoContactWithPrimary,
  QuoteDeclineReason,
  Case,
  TimestampWithZone,
  OffOCSPayload,
  ReviewType,
  AssignUserToGroupType,
  CloseCaseParams,
  BulkAssignmentPayloadOperation,
  TxCenterORData,
  SupplementalOrganRecord,
  EmployeeLabels,
} from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { refetchRouteLogistics } from '@/queries/route-planner/utils';
import { caseQueryKeys } from './case.keys';

export const useUpdateCaseMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, ...restVars }: Partial<NewCase>) =>
      'reAllocationCaseId' in restVars
        ? api.case.updateReallocated(caseId || '', restVars)
        : api.case.updateCase(caseId || '', restVars),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data, variables) => {
      await updateCaseCache(data);
      // donorId + organType determine duplicates, so resync the list whenever either is edited.
      if ('organType' in variables || 'donorId' in variables) {
        updateCaseDuplicatesCache(data);
      }
    },
  });
};

export const useUpdateTxCenterORMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, ...restVars }: Pick<Case, 'caseId'> & Partial<TxCenterORData>) =>
      api.case.updateTxCenterOR(caseId, restVars),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useCreateDraftCaseMutation = () => {
  return useMutation({
    mutationFn: (variables: CreateDraftCaseType) =>
      'reAllocationCaseId' in variables
        ? api.case.createReallocationDraft(variables)
        : api.case.createDraftCase(variables),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: (data) => {
      queryClient.setQueryData(caseQueries.item(data.caseId).queryKey, data);
      reconcileCaseAcrossListCaches(data);
    },
  });
};

export const useCreateClinicalScreeningCaseMutation = () => {
  return useMutation({
    mutationFn: (variables: CreateDraftCaseType) =>
      'reAllocationCaseId' in variables
        ? api.case.createReallocationClinicalScreening(variables)
        : api.case.createClinicalScreeningCase(variables),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: (data) => {
      queryClient.setQueryData(caseQueries.item(data.caseId).queryKey, data);
      reconcileCaseAcrossListCaches(data);
    },
  });
};

export const useCreateTxCenterDraftCaseMutation = (canAddCaseToList: boolean = true) => {
  return useMutation({
    mutationFn: (variables: CreateTxCenterDraftCaseType) => api.case.createTxCenterDraftCase(variables),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: (data) => {
      queryClient.setQueryData(caseQueries.item(data.caseId).queryKey, data);
      if (canAddCaseToList) {
        reconcileCaseAcrossListCaches(data);
      }
    },
  });
};

export const useDeleteDraftCaseMutation = () => {
  return useMutation({
    mutationFn: (caseId: string) => api.case.deleteDraftCase(caseId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: (data) => {
      removeCaseFromAllListCaches(data.caseId);
    },
  });
};

type AddCaseNoteMutationVars = {
  caseId: string;
  message: string;
};
export const useCreateCaseNoteMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, message }: AddCaseNoteMutationVars) => api.case.addCaseNote(caseId, { message }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

type UpdateCaseNoteMutationVars = {
  caseId: string;
  noteId: string;
  message: string;
};
export const useUpdateCaseNoteMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, noteId, message }: UpdateCaseNoteMutationVars) =>
      api.case.updateCaseNote(caseId, noteId, { message }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

type DeleteCaseNoteMutationVars = {
  caseId: string;
  noteId: string;
};
export const useDeleteCaseNoteMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, noteId }: DeleteCaseNoteMutationVars) => api.case.deleteCaseNote(caseId, noteId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

type PinCaseNoteMutationVars = {
  caseId: string;
  noteId: string;
  isPinned: boolean;
};
export const usePinCaseNoteMutationMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, noteId, isPinned }: PinCaseNoteMutationVars) =>
      isPinned ? api.case.pinCaseNote(caseId, noteId) : api.case.unpinCaseNote(caseId, noteId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useMoveCaseToNextStatusMutation = () =>
  useMutation({
    mutationFn: ({ caseId, nextStatus }: { caseId: Uuid; nextStatus: CaseStatus }) =>
      getNextCaseStatusMutation(caseId, nextStatus),
    retry: shouldRetryOnError,
    retryDelay,
    meta: {
      isGlobalLoader: true,
    },
    onSuccess: (updatedCase) => {
      updateCaseCache(updatedCase);
      refetchDuplicatesIfCaseClosed(updatedCase.status);
    },
  });

export const useMoveCasesToClosedStatusMutation = () =>
  useMutation({
    mutationFn: (params: CloseCaseParams) => api.case.changeStatusCasesClosed(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: caseQueryKeys.getArchive(),
      });
    },
  });

export const useDeclineCase = () =>
  useMutation({
    mutationFn: ({ caseId, dispositionComment }: { caseId: Uuid; dispositionComment: string }) =>
      api.case.changeStatusDeclined(caseId, dispositionComment),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });

export const useDeclineService = () =>
  useMutation({
    mutationFn: ({ caseId, data }: { caseId: Uuid; data: DeclineCaseParams }) =>
      api.case.changeStatusServiceDeclined(caseId, data),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });

export const useOnOcs = () =>
  useMutation({
    mutationFn: ({ caseId, data }: { caseId: Uuid; data: TimestampWithZone }) =>
      api.case.changeStatusOnOcs(caseId, data),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });

export const useOffOcs = () =>
  useMutation({
    mutationFn: ({ caseId, data }: { caseId: Uuid; data: OffOCSPayload }) => api.case.changeStatusOffOcs(caseId, data),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });

export const useMoveRouteToNextStatusMutation = () =>
  useMutation({
    mutationFn: ({
      caseId,
      currentStatus,
      comment,
      isSendQuoteInternalMain,
    }: {
      caseId: Uuid;
      currentStatus: RoutePlanStatus;
      comment?: string;
      isSendQuoteInternalMain?: boolean;
    }) => getNextRouteStatusMutation(caseId, currentStatus, comment, isSendQuoteInternalMain),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });

type RejectAviationMutationVars = {
  caseId: string;
  comment?: string;
};

export const useRejectAviationMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, comment }: RejectAviationMutationVars) => api.case.setRouteRejectAviation(caseId, comment),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const useSetRoutePlanning = () => {
  return useMutation({
    mutationFn: ({ caseId }: { caseId: string }) => api.case.setRoutePlanning(caseId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

const checkApproveGroundInCase = async (caseId: string): Promise<Case> => {
  return new Promise((resolve) => {
    const intervalId = setInterval(async () => {
      const updatedCase = await api.case.getCaseById(caseId);

      if (updatedCase.reviews.length > 0 && updatedCase.reviews.some((r) => r.type === ReviewType.Ground)) {
        resolve(updatedCase as Case);
        clearInterval(intervalId);
      }
    }, 1000);
  });
};

const setRouteApproveGround = async (caseId: string): Promise<Case> => {
  await api.case.setRouteApproveGround(caseId);

  return checkApproveGroundInCase(caseId);
};

export const useGroundApprove = () => {
  return useMutation({
    mutationFn: ({ caseId }: { caseId: Uuid }) => setRouteApproveGround(caseId),
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const usePendingResourcesBooking = () => {
  return useMutation({
    mutationFn: ({ caseId }: { caseId: Uuid }) => api.case.setRouteBookingPending(caseId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const useRoutePlanningDone = () => {
  return useMutation({
    mutationFn: ({ caseId }: { caseId: Uuid }) => api.case.setRoutePlanningDone(caseId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const useAssignUserToCaseGroup = () => {
  return useMutation({
    mutationFn: ({ caseId, payload }: { caseId: Uuid; payload: AssignUserToGroupType[] }) =>
      api.case.assignUsersToGroup(caseId, payload),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useAssignOpoContactsToCase = () => {
  return useMutation({
    mutationFn: ({ caseId, contacts }: { caseId: Uuid; contacts: OpoContactWithPrimary[] }) =>
      api.case.assignOpoContacts(caseId, contacts),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useRemoveOpoContactsAssignment = () => {
  return useMutation({
    mutationFn: ({ caseId, contactIds }: { caseId: Uuid; contactIds: string[] }) =>
      api.case.removeOpoContacts(caseId, contactIds),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

type QuoteApproveMutationVars = {
  caseId: string;
};

// TODO: as used on customer portal - should be moved to quotes after finishing on logistic platform
export const useApproveQuoteByTxMutation = () => {
  return useMutation({
    mutationFn: ({ caseId }: QuoteApproveMutationVars) => api.case.approveQuoteByTx(caseId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data) => {
      await updateCaseCache(data);
    },
  });
};

type QuoteRejectMutationVars = {
  caseId: string;
  comment: string;
  reason: QuoteDeclineReason;
};

export const useRejectQuoteByTxMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, comment, reason }: QuoteRejectMutationVars) =>
      api.case.rejectQuoteByTx(caseId, { comment, reason }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useAddCaseToFavorite = () => {
  return useMutation({
    mutationFn: ({ caseId }: { caseId: string }) => api.case.addFavorite(caseId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: (_, vars) => {
      updateCaseField(vars.caseId, 'isFavourite', true);
    },
  });
};

export const useRemoveCaseFromFavorite = () => {
  return useMutation({
    mutationFn: ({ caseId }: { caseId: string }) => api.case.removeFavorite(caseId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: (_, vars) => {
      updateCaseField(vars.caseId, 'isFavourite', false);
    },
  });
};

export const useAddLink = () => {
  return useMutation({
    mutationFn: ({ caseId, linkData }: { caseId: string; linkData: LinkBody }) => api.case.postLink(caseId, linkData),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useChangeLink = () => {
  return useMutation({
    mutationFn: ({ caseId, linkId, linkData }: { caseId: string; linkId: string; linkData: LinkBody }) =>
      api.case.patchLink(caseId, linkId, linkData),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useDeleteLink = () => {
  return useMutation({
    mutationFn: ({ caseId, linkId }: { caseId: string; linkId: string }) => api.case.deleteLink(caseId, linkId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useUpdateWolsMutation = () => {
  return useMutation({
    mutationFn: async ({
      caseId,
      text,
      wolsType,
      id,
    }: {
      caseId: string;
      text: string | null;
      wolsType: WOLSType;
      id: string | undefined;
    }) => {
      const resp = await api.case.updateWols(caseId, text, wolsType, id);
      return toClinicalRecord(resp);
    },
    onSuccess: (record, vars) => {
      updateClinicalInCaseCache(vars.caseId, record);
    },
  });
};

export const useUpdateNrpMutation = () => {
  return useMutation({
    mutationFn: async ({
      id,
      caseId,
      nrp,
      nrpType,
    }: {
      id: string | undefined;
      caseId: string;
      nrp: boolean;
      nrpType: NRPType;
    }) => {
      const resp = await api.case.updateNrpType(caseId, nrp, nrpType, id);
      return toClinicalRecord(resp);
    },
    onSuccess: (record, vars) => {
      updateClinicalInCaseCache(vars.caseId, record);
    },
  });
};

export const useCopyClinicalDataMutation = () => {
  return useMutation({
    mutationFn: (caseId: string) => api.case.copyClinicalData(caseId),
    retry: shouldRetryOnError,
    retryDelay,
  });
};

export const useUpdateSupplementalOrganMutation = () => {
  return useMutation({
    mutationFn: async ({
      caseId,
      kidney,
      pancreas,
      prevRecord,
    }: {
      caseId: string;
      kidney?: boolean;
      pancreas: boolean;
      prevRecord?: SupplementalOrganRecord;
    }) => {
      const resp = await api.case.updateSupplementalOrgan(caseId, kidney, pancreas, prevRecord);
      return toClinicalRecord(resp);
    },
    onSuccess: (record, vars) => {
      updateClinicalInCaseCache(vars.caseId, record);
    },
  });
};

export const useSetAssignedCsLabel = () => {
  return useMutation({
    mutationFn: ({ caseId, employeeId, labels }: { caseId: string; employeeId: string; labels: EmployeeLabels[] }) => {
      return api.case.setAssignedCSLabel(caseId, employeeId, { labels });
    },
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useChangeCsStatus = () => {
  return useMutation({
    mutationFn: ({
      caseId,
      employeeId,
      status,
    }: {
      caseId: string;
      employeeId: string;
      status: ResourceLogisticsStatus;
    }) => {
      const mutation = getCsStatusMutation(status);
      return mutation(caseId, employeeId);
    },
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useChangeSurgeonStatus = () => {
  return useMutation({
    mutationFn: ({
      caseId,
      employeeId,
      status,
    }: {
      caseId: string;
      employeeId: string;
      status: ResourceLogisticsStatus;
    }) => {
      const mutation = getSurgeonStatusMutation(status);
      return mutation(caseId, employeeId);
    },
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useChangeDeviceStatus = () => {
  return useMutation({
    mutationFn: ({
      caseId,
      consoleId,
      status,
    }: {
      caseId: string;
      consoleId: string;
      status: ResourceLogisticsStatus;
    }) => {
      const mutation = getDeviceStatusMutation(status);
      return mutation(caseId, consoleId);
    },
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useDeclineOrganMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, timeZone, declineReason }: { caseId: string; timeZone: string; declineReason: string }) =>
      api.case.declineOrgan(caseId, { timeZone, declineReason }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useApproveOrganMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, timeZone }: { caseId: string; timeZone: string }) =>
      api.case.approveOrgan(caseId, { timeZone }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useDeclineServiceMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, comment }: { caseId: string; comment: string | null }) =>
      api.case.declineService(caseId, { comment }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useApproveServiceMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, comment }: { caseId: string; comment: string | null }) =>
      api.case.approveService(caseId, { comment }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const useLightsAndSirensMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, isLightsSirensRequired }: { caseId: string; isLightsSirensRequired: boolean }) =>
      api.case.setLightsAndSirensRequest(caseId, isLightsSirensRequired),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const useNonStopFlightMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, nonStopFlightRequired }: { caseId: string; nonStopFlightRequired: boolean }) =>
      api.case.setNonStopFlightRequired(caseId, nonStopFlightRequired),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const useQuestionaryEditAllowanceMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, isQuestionaryEditAllowed }: { caseId: string; isQuestionaryEditAllowed: boolean }) =>
      api.case.setQuestionaryEditAllowanceRequest(caseId, isQuestionaryEditAllowed),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: updateCaseCache,
  });
};

export const usePatchBulkAssignment = () => {
  return useMutation({
    mutationFn: (payload: BulkAssignmentPayloadOperation[]) => api.case.patchBulkAssignment(payload),
  });
};

const refreshAccessTickets = async (caseId?: string) => {
  if (!caseId) return;

  const key = caseQueryKeys.getAccessTickets(caseId);

  await queryClient.refetchQueries({
    queryKey: key,
    exact: true,
    type: 'active',
  });
};

export const useGrantArchiveAccessTicketsMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, forUserId }: { caseId: string; forUserId: string }) =>
      api.case.accessTicketsGrant(caseId, { forUserId }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (_data, variables) => {
      await refreshAccessTickets(variables.caseId);
    },
  });
};

export const useGrantArchiveAccessTicketsInfiniteMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, forUserId }: { caseId: string; forUserId: string }) =>
      api.case.accessTicketsGrantInfinite(caseId, { forUserId }),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (_data, variables) => {
      await refreshAccessTickets(variables.caseId);
    },
  });
};

export const useAccessTicketsRevoke = () => {
  return useMutation({
    mutationFn: ({ caseId, userId }: { caseId: string; userId: string }) =>
      api.case.accessTicketsRevoke(caseId, userId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (_data, variables) => {
      await refreshAccessTickets(variables.caseId);
    },
  });
};

export const useAccessTicketsRevokeInfinite = () => {
  return useMutation({
    mutationFn: ({ caseId, userId }: { caseId: string; userId: string }) =>
      api.case.accessTicketsRevokeInfinite(caseId, userId),
    retry: shouldRetryOnError,
    retryDelay,
    onSuccess: async (_data, variables) => {
      await refreshAccessTickets(variables.caseId);
    },
  });
};
